import { addScript } from '@tunein/web-utils';
import { noop } from 'es-toolkit';
import { recaptchaSiteKey } from '../../constants/auth/recaptcha';
import { isDiscordMode } from '../discord';

function readyCallback(action, resolve) {
  return async () => {
    const token = await window.grecaptcha.execute(recaptchaSiteKey, { action });
    resolve(token);
  };
}

const RECAPTCHA_V3_SCRIPT_SRC = `https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`;
let recaptchaV3ScriptPromise = null;

/**
 * Loads reCAPTCHA v3 once; concurrent and later calls share the load. Discord never loads it.
 */
export async function loadRecaptchaV3Script() {
  if (typeof window === 'undefined' || typeof document === 'undefined' || isDiscordMode()) {
    return;
  }

  if (!recaptchaV3ScriptPromise) {
    recaptchaV3ScriptPromise = addScript({ src: RECAPTCHA_V3_SCRIPT_SRC, async: true });
  }

  try {
    await recaptchaV3ScriptPromise;
  } catch (error) {
    recaptchaV3ScriptPromise = null;

    throw error;
  }
}

const IDLE_TIMEOUT_MS = 10_000;
const NO_IDLE_CALLBACK_DELAY_MS = 3_000;

/**
 * Loads reCAPTCHA v3 once the main thread is idle. It runs on every page because v3 scores sign-ins
 * and sign-ups better when it has observed the visit, not only the form; waiting keeps it out of
 * the page's own loading work.
 */
export function loadRecaptchaV3WhenIdle() {
  const load = () => loadRecaptchaV3Script().catch(noop);

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(load, { timeout: IDLE_TIMEOUT_MS });
  } else {
    setTimeout(load, NO_IDLE_CALLBACK_DELAY_MS);
  }
}

const RECAPTCHA_V2_SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit';
let recaptchaV2ScriptPromise = null;

export async function loadRecaptchaV2Script() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve();
  }

  if (recaptchaV2ScriptPromise) {
    return recaptchaV2ScriptPromise;
  }

  if (window.grecaptcha && typeof window.grecaptcha.render === 'function') {
    recaptchaV2ScriptPromise = Promise.resolve();
    return recaptchaV2ScriptPromise;
  }

  try {
    recaptchaV2ScriptPromise = addScript({
      src: RECAPTCHA_V2_SCRIPT_SRC,
      async: true,
      attributes: { defer: 'true' },
    });

    await recaptchaV2ScriptPromise;
  } catch (error) {
    recaptchaV2ScriptPromise = null;

    throw error;
  }
}

export async function getRecaptchaToken(action, onError) {
  try {
    await loadRecaptchaV3Script();
  } catch {
    onError({
      message: 'Recaptcha V3: failed to load script',
      context: { action },
    });
    return null;
  }

  if (!window.grecaptcha) {
    onError({
      message: 'Recaptcha V3: grecaptcha not defined',
      context: { action },
    });
    return null;
  }

  return new Promise((resolve) => {
    try {
      window.grecaptcha.ready(readyCallback(action, resolve));
    } catch (e) {
      onError({
        message: 'Recaptcha V3: error fetching token',
        context: { action },
      });
      resolve(null);
    }
  });
}
